import React, { useContext, useState } from "react";
import {
  Row,
  Grid,
  Card,
  Button,
  ViewContext,
  Loader,
} from "components/lib";
import { useNavigate } from "react-router-dom";
import Axios from "axios";
import ConfirmDialog from "./components/ConfirmDialog";

function uuidv4() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Format the ASIC/ABN identifiers on a search result for display.
function identifierLine(org) {
  const parts = [];
  if (org?.extra?.abn) parts.push(`ABN ${org.extra.abn}`);
  if (org?.extra?.acn) parts.push(`ACN ${org.extra.acn}`);
  return parts.join(" · ");
}

export function EntityOnboardingStartView() {
  const navigate = useNavigate();
  const viewContext = useContext(ViewContext);
  const [phase, setPhase] = useState("entry"); // entry | results | confirm
  const [term, setTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState([]); // [{ organisation, historical_reports, confirmation_token }]
  const [selected, setSelected] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const closeConfirm = () => setConfirmDialog(null);

  const canSearch = term.trim().replace(/\s/g, "").length >= 3;

  async function handleSearch(e) {
    e?.preventDefault?.();
    if (!canSearch) return;
    setSubmitting(true);
    try {
      const res = await Axios.post("/api/entity-onboarding/lookup", {
        term: term.trim(),
      });
      const list = res.data.results || [];
      setResults(list);
      if (list.length === 1) {
        setSelected(list[0]);
        setPhase("confirm");
      } else {
        setSelected(null);
        setPhase("results");
      }
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Search failed",
        "error",
        true
      );
    } finally {
      setSubmitting(false);
    }
  }

  function selectResult(result) {
    setSelected(result);
    setPhase("confirm");
  }

  async function reuseExisting(report_id) {
    setSubmitting(true);
    try {
      const res = await Axios.post("/api/entity-onboarding/ubo-reports", {
        confirmation_token: selected.confirmation_token,
        reuse_report_id: report_id,
      });
      navigate(`/entity-onboarding/reports/${res.data.report_id}`);
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Failed to reuse report",
        "error",
        true
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function pullFresh() {
    setSubmitting(true);
    try {
      const idem = uuidv4();
      const res = await Axios.post("/api/entity-onboarding/ubo-reports", {
        confirmation_token: selected.confirmation_token,
        idempotency_key: idem,
      });
      if (res.data.can_force_fresh) {
        setSubmitting(false);
        setConfirmDialog({
          title: "Pull a brand-new UBO report?",
          body: (
            <>
              <p>
                A report with this confirmation already exists.
              </p>
              <p className="mt-2">
                Pulling a fresh one is a <strong>billable</strong> CreditorWatch
                lookup. Continue only if you need the most up-to-date UBOs.
              </p>
            </>
          ),
          confirmText: "Pull fresh report",
          onConfirm: async () => {
            closeConfirm();
            setSubmitting(true);
            try {
              const res2 = await Axios.post("/api/entity-onboarding/ubo-reports", {
                confirmation_token: selected.confirmation_token,
                idempotency_key: uuidv4(),
                force_fresh: true,
              });
              navigate(`/entity-onboarding/reports/${res2.data.report_id}`);
            } catch (err) {
              viewContext.notification.show(
                err.response?.data?.message || "Failed to start report",
                "error",
                true
              );
            } finally {
              setSubmitting(false);
            }
          },
        });
        return;
      }
      navigate(`/entity-onboarding/reports/${res.data.report_id}`);
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Failed to start report",
        "error",
        true
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (phase === "entry") {
    return (
      <Grid>
        <Row>
          <h1 className="text-2xl font-bold">Entity Onboarding</h1>
        </Row>
        <Card>
          <form onSubmit={handleSearch} className="flex flex-col gap-4">
            <label className="text-sm font-medium">
              Search by company name, ABN, or ACN
            </label>
            <input
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="e.g. Lobriza Holdings, 51 824 753 556, or 626 416 251"
              className="border rounded px-3 py-2"
              autoFocus
            />
            <div className="flex items-center gap-3 text-sm text-gray-500">
              {term && !canSearch && (
                <span>Enter at least 3 characters to search</span>
              )}
            </div>
            <Button
              text={submitting ? "Searching..." : "Search"}
              color="blue"
              action={handleSearch}
              disabled={!canSearch || submitting}
            />
          </form>
        </Card>

        <ConfirmDialog
          open={!!confirmDialog}
          title={confirmDialog?.title}
          body={confirmDialog?.body}
          confirmText={confirmDialog?.confirmText}
          cancelText={confirmDialog?.cancelText}
          onConfirm={confirmDialog?.onConfirm}
          onCancel={closeConfirm}
        />
      </Grid>
    );
  }

  if (phase === "results") {
    return (
      <Grid>
        <Row>
          <h1 className="text-2xl font-bold">Select organisation</h1>
        </Row>

        {results.length === 0 ? (
          <Card>
            <p className="text-sm text-gray-600">
              No matches found for “{term.trim()}”. Try a different name, ABN, or ACN.
            </p>
          </Card>
        ) : (
          <Card>
            <p className="text-sm text-gray-600 mb-3">
              {results.length} match{results.length === 1 ? "" : "es"} for “
              {term.trim()}”. Select the organisation you want to onboard.
            </p>
            <ul className="divide-y">
              {results.map((r, i) => {
                const org = r.organisation;
                return (
                  <li
                    key={org.identifier || i}
                    className="py-3 flex items-center justify-between gap-3"
                  >
                    <div className="text-sm">
                      <div className="font-semibold">{org.entity_name}</div>
                      <div className="text-gray-500">
                        {[org.entity_type, org.status && `Status: ${org.status}`]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                      <div className="text-gray-500">
                        {[identifierLine(org), [org.address?.state, org.address?.postcode]
                          .filter(Boolean)
                          .join(" ")]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                    <Button
                      small
                      text="Select"
                      color="blue"
                      action={() => selectResult(r)}
                      disabled={submitting}
                    />
                  </li>
                );
              })}
            </ul>
          </Card>
        )}

        <Row>
          <Button
            text="Back to search"
            outline
            action={() => {
              setPhase("entry");
              setResults([]);
              setSelected(null);
            }}
            disabled={submitting}
          />
        </Row>
      </Grid>
    );
  }

  // phase === 'confirm'
  const org = selected.organisation;
  const historical = selected.historical_reports || [];

  return (
    <Grid>
      <Row>
        <h1 className="text-2xl font-bold">Confirm organisation</h1>
      </Row>

      <Card>
        <div className="flex flex-col gap-2">
          <div className="text-lg font-semibold">{org.entity_name}</div>
          <div className="text-sm text-gray-600">
            {[org.entity_type, org.status && `Status: ${org.status}`]
              .filter(Boolean)
              .join(" · ")}
          </div>
          {identifierLine(org) && (
            <div className="text-sm text-gray-600">{identifierLine(org)}</div>
          )}
          <div className="text-sm text-gray-600">
            {[org.address?.state, org.address?.postcode].filter(Boolean).join(" ")}
          </div>
          {org.business_names?.length > 0 && (
            <div className="text-sm text-gray-600">
              Trading as: {org.business_names.join(", ")}
            </div>
          )}
        </div>
      </Card>

      {historical.length > 0 && (
        <Card>
          <h2 className="text-lg font-semibold mb-2">
            We already have UBO reports for this entity
          </h2>
          <p className="text-sm text-gray-600 mb-3">
            UBOs can change over time. Reuse a recent report (no charge), or pull
            a fresh one (billable).
          </p>
          <ul className="divide-y">
            {historical.map((h) => (
              <li
                key={h.id}
                className="py-2 flex items-center justify-between gap-3"
              >
                <div className="text-sm">
                  <div>{new Date(h.created_at).toLocaleString()}</div>
                  <div className="text-gray-500">
                    {h.person_count ?? "?"} UBOs · {h.status}
                  </div>
                </div>
                <Button
                  small
                  text="Reuse"
                  color="green"
                  action={() => reuseExisting(h.id)}
                  disabled={submitting}
                />
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        <h2 className="text-lg font-semibold mb-2">Pull a fresh UBO report</h2>
        <p className="text-sm text-gray-600 mb-3">
          This is a billable event. We will request a fresh UBO report from
          CreditorWatch.
        </p>
        <div className="flex gap-2">
          <Button
            text="Pull fresh report"
            color="blue"
            action={pullFresh}
            disabled={submitting}
          />
          <Button
            text={results.length > 1 ? "Back to results" : "Cancel"}
            outline
            action={() => {
              if (results.length > 1) {
                setPhase("results");
                setSelected(null);
              } else {
                setPhase("entry");
                setResults([]);
                setSelected(null);
              }
            }}
            disabled={submitting}
          />
        </div>
      </Card>

      {submitting && <Loader />}

      <ConfirmDialog
        open={!!confirmDialog}
        title={confirmDialog?.title}
        body={confirmDialog?.body}
        confirmText={confirmDialog?.confirmText}
        cancelText={confirmDialog?.cancelText}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={closeConfirm}
      />
    </Grid>
  );
}

export default EntityOnboardingStartView;
